import { any, curry, filter, map } from 'ramda';
import qs, { ParsedQs } from 'qs';

import Cookies from 'js-cookie';
import { EN_APP_DEVICE } from './functions/device';
import { EnvConfig } from './feature-flag';
import { IRequestWrap } from '../server/IServer';
import { IRequesterError } from '../services/RequestService/IRequester';
import { UAParser } from 'ua-parser-js';
import { YaMo } from '@/services/YaMoment';
import _ from 'lodash';
import { checkIsBrowser } from './functions/checkServerEnvironment';
import copy from 'copy-to-clipboard';
import debounce from 'lodash/debounce';
import { getYaNative } from '@yanolja-rnd/ya-native';
import { hasItems } from '@/utils/functions/hasItems';
import moment from 'moment-timezone';
import throttle from 'lodash/throttle';

export { linkClickHandler } from './functions/eventHandler/linkClickHandler';
export { prevent } from './functions/eventHandler/prevent';
export { preventAndThrottle } from './functions/eventHandler/preventAndThrottle';
export { stopBubbling } from './functions/eventHandler/stopBubbling';
export { formatPriceInfo } from './functions/formatPriceInfo';
export { hasItems } from './functions/hasItems';
export { hasNoItems } from './functions/hasNoItems';
export { joinURL } from './functions/joinURL';
export { numberFormat } from './functions/numberFormat';
export { rejectNilKeys } from './functions/rejectNilKeys';
export { roundTo } from './functions/roundTo';
export { waitFor } from './functions/waitFor';
export { debounce, throttle };
export { IS_PRODUCTION_ENV } from './functions/IS_PRODUCTION_ENV';

export const IOS_INSTALL = 'https://yanolja.onelink.me/tmFm';
export const ANDROID_INSTALL_WEB = 'https://play.google.com/store/apps/details?id=com.cultsotry.yanolja.nativeapp';
export const ANDROID_INSTALL = 'market://details?id=com.cultsotry.yanolja.nativeapp';

/**
 * 지도 마커 유형
 */
export enum EN_MARKER_TYPE {
  NORMAL,
  MOTEL,
  HOTEL,
  PENSION,
  GUESTHOUSE,
  YAJA,
  YAM,
  AVENUE,
  KOTEL,
  NO_ASSOCIATION,
  HERE,
}

/**
 * 지도 마커 유형에 따른 URL
 */
export enum EN_MARKER_ICON_URL {
  LOCATION = '/static/images/map/marker-location.svg',
  LEISURE = '/static/images/marker-leisure.svg',
  NORMAL = '/static/images/map/marker-normal.svg',
  MOTEL = '/static/images/map/marker-accomodation-pin.svg',
  HOTEL = '/static/images/map/marker-accomodation-pin.svg',
  PENSION = '/static/images/map/marker-accomodation-pin.svg',
  GUESTHOUSE = '/static/images/map/marker-accomodation-pin.svg',
  YAJA = '/static/images/map/marker-accomodation-pin.svg',
  YAM = '/static/images/map/marker-accomodation-pin.svg',
  AVENUE = '/static/images/map/marker-accomodation-pin.svg',
  KOTEL = '/static/images/map/marker-accomodation-pin.svg',
  NO_ASSOCIATION = '/static/images/map/marker-default.svg',
  HERE = '/static/images/map/marker-here.svg',
}

export enum LOCATION_TYPE {
  DEFAULT = 'DEFAULT',
  GPS = 'GPS',
  MANUAL = 'MANUAL',
}

export interface IGeoLocation {
  latitude: string;
  longitude: string;
  locationType: LOCATION_TYPE;
  locationTime: number;
  isFailed?: boolean;
}

export interface ICookieLocation {
  latitude?: string;
  longitude?: string;
  locationType?: LOCATION_TYPE;
  locationTime?: number;
  address?: string;
  addressShort?: string;
  addressOnlyRoad?: string;
  hasPermissionError?: boolean;
}

export const COOKIE_LOCATION_KEY = 'location';

// TODO: 나중에 상수부분은 공통로직으로
export const DEFAULT_LOCATION: IGeoLocation = {
  latitude: '37.50681',
  longitude: '127.06624',
  locationType: LOCATION_TYPE.DEFAULT,
  locationTime: YaMo.getSeoulTimeUnix(),
  isFailed: true,
};

export function getToday(): moment.Moment {
  return moment().tz('Asia/Seoul').subtract(2, 'hours');
}

export function getTimeZone(time?: string): moment.Moment {
  return time ? moment(time).tz('Asia/Seoul') : moment.tz('Asia/Seoul');
}

/**
 * 위경도, 주소정보를 쿠키에서 읽음
 *
 * @return location 경도 위도 주소정보
 */
export function getCookieLocation(): ICookieLocation {
  let parsedCookieLocation = {};
  try {
    const cookieLocation = Cookies.get(COOKIE_LOCATION_KEY);
    if (cookieLocation) {
      parsedCookieLocation = JSON.parse(cookieLocation);
    }
  } catch (_) {}
  return parsedCookieLocation;
}

/**
 * 다양한 날짜 관련 값을 Date객체로 반환한다.
 * Safari에서 2020.07.02 같은 포맷이 변환이 안되는 현상일때 사용 함
 */
export function parseDate(input, format = 'yyyy-mm-dd'): Date {
  const parts = input.match(/(\d+)/g);
  const fmt = {};
  let i = 0;

  format.replace(/(yyyy|dd|mm)/g, (part): any => {
    fmt[part] = i++;
  });

  return new Date(parts[fmt['yyyy']], parts[fmt['mm']] - 1, parts[fmt['dd']]);
}

/**
 * 위경도, 주소정보를 쿠키에 저장
 *
 * @param location 경도 위도 주소정보
 * @param force true 이면 기존 cookie 에 덮어쓰지 않고 새로 저장
 */
export function setCookieLocation(location: ICookieLocation, force: boolean = false): void {
  const cookieLocation = getCookieLocation();
  try {
    if ((location && isNaN(Number(location.latitude || 0))) || isNaN(Number(location.longitude || 0))) {
      return;
    }

    if (cookieLocation) {
      const expires = new Date();
      expires.setTime(expires.getTime() + 1000 * 60 * 10); // 10분

      if (force) {
        Cookies.set(COOKIE_LOCATION_KEY, JSON.stringify(location), { expires });
      } else {
        Cookies.set(COOKIE_LOCATION_KEY, JSON.stringify({ ...cookieLocation, ...location }), { expires });
      }
    }
  } catch (_) {
    Cookies.remove(COOKIE_LOCATION_KEY);
  }
}

/**
 * 위경도, 주소정보 쿠키 삭제
 *
 * @param type 위경도 | 주소 | 전체
 */
export function clearCookieLocation(type: 'coords' | 'address' | '' = ''): void {
  const cookieLocation = getCookieLocation();
  if (type === 'coords') {
    if (cookieLocation.latitude && cookieLocation.longitude) {
      delete cookieLocation.latitude;
      delete cookieLocation.longitude;
      delete cookieLocation.locationType;
      delete cookieLocation.locationTime;
    }
    setCookieLocation(cookieLocation, true);
  } else if (type === 'address') {
    if (cookieLocation.address && cookieLocation.addressShort) {
      delete cookieLocation.address;
      delete cookieLocation.addressShort;
    }
    setCookieLocation(cookieLocation, true);
  } else {
    Cookies.remove(COOKIE_LOCATION_KEY);
  }
}

function getCurrentPositionFail(): IGeoLocation {
  const cookieLocation = getCookieLocation();
  const { latitude, longitude, locationType, locationTime } = cookieLocation;
  if (latitude && longitude) {
    return {
      latitude,
      longitude,
      locationType:
        locationType || isDefaultLocation({ latitude, longitude }) ? LOCATION_TYPE.DEFAULT : LOCATION_TYPE.MANUAL,
      locationTime: locationTime || YaMo.getSeoulTimeUnix(),
      isFailed: DEFAULT_LOCATION.isFailed,
    };
  }
  return DEFAULT_LOCATION;
}

/**
 * @param force
 * @param isAvailable 로그인 && 위치정보 약관 동의
 * @param errorHandler
 */
export function getLocation(
  force: boolean = false,
  isAvailable: boolean = false,
  errorHandler?: (error: PositionError) => void,
): Promise<IGeoLocation> {
  const options = {
    enableHighAccuracy: false, // 정확도 여부
    maximumAge: 10000, // 캐시유지 시간
    timeout: 5000, // 타임아웃
  };

  return new Promise<IGeoLocation>((resolve) => {
    let watchId;

    const clearWatch = (): void => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };

    const onSuccess = (position): void => {
      clearWatch();
      const { latitude, longitude } = position.coords;

      return resolve({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        locationType: LOCATION_TYPE.GPS,
        locationTime: YaMo.getSeoulTimeUnix(),
      });
    };

    const onError = (positionError: PositionError): void => {
      errorHandler && errorHandler(positionError);

      clearWatch();
      resolve(getCurrentPositionFail());
    };

    const getCurrentLocation = (): void => {
      if (!isAvailable) {
        return resolve(getCurrentPositionFail());
      }

      watchId = navigator.geolocation.watchPosition(onSuccess, onError, options);
      navigator.geolocation.getCurrentPosition(onSuccess, onError, options);
      // iOS에서 위치정보 꺼져있을 때 getCurrentPosition 타임아웃이 안됨..?
      setTimeout(() => {
        clearWatch();
        resolve(DEFAULT_LOCATION);
      }, options.timeout);
    };

    /**
     * 위치정보 갱신 동작 정책
     * https://yanolja.atlassian.net/wiki/spaces/FES/pages/103744820
     */
    if (isAvailable && force) {
      clearCookieLocation();
      getCurrentLocation();
    } else {
      getCurrentLocation();
    }
  }).catch(() => {
    return Promise.resolve(getCurrentPositionFail());
  });
}

/**
 * @desc 브라우저의 위치 권한 동의 여부를 반환하는 함수
 * @return PermissionState
 */
export const getLocationPermission = async (): Promise<PermissionState | undefined> => {
  try {
    const { state } = await navigator.permissions.query({ name: 'geolocation' });
    return state;
  } catch {
    return undefined;
  }
};

/**
 * @desc DEFAULT_LOCATION의 위경도와 전달받은 위경도가 같은지 비교하는 함수
 * @returns boolean
 */
export function isDefaultLocation({
  latitude,
  longitude,
}: Partial<Pick<IGeoLocation, 'latitude' | 'longitude'>>): boolean {
  if (!latitude || !longitude) {
    return true;
  }

  if (isNaN(Number(latitude)) || isNaN(Number(longitude))) {
    return true;
  }

  return latitude === DEFAULT_LOCATION.latitude && longitude === DEFAULT_LOCATION.longitude;
}

/**
 * `document.referrer`가 야놀자 웹(`https://*.yanolja*`)인지 여부를 반환한다.
 *
 * 단, 오류 페이지(`/404` 등)면 거짓을 반환한다.
 */
export const isReferrerYaWeb = (): boolean =>
  !!document.referrer && /https?:\/\/[a-z_\-\.]+\.yanolja\.com(:3000)?\/(?![4-5][0-9]{2})/.test(document.referrer);

/**
 * `document.referrer`가 야놀자 웹이면 `history.back()`을 호출한다.
 * @param path 이동할 경로.
 * @param exclude 추가로 `referrer`에서 제외할 때 사용할 정규표현식.
 */
export const forceBackFromExternal = (path: string, exclude?: RegExp): void => {
  if (isReferrerYaWeb() && (exclude ? !exclude.test(document.referrer) : true)) {
    return history.back();
  }
  location.replace(path);
};

export const APP_ENTRY_URL = 'APP_ENTRY_URL';
/**
 * 웹뷰의 최초 페이지를 sessionStorage에 저장한다.
 * @see `SunnyPage`
 * @see `forceBackWebView`
 */
export const setWebViewEntryURL = (): void => {
  if (!getYaNative().isEnabled) {
    return;
  }
  if (!sessionStorage.getItem(APP_ENTRY_URL)) {
    sessionStorage.setItem(APP_ENTRY_URL, location.href);
  }
};

export const removeWebViewEntryURL = (): void => {
  if (!getYaNative().isEnabled) {
    return;
  }
  sessionStorage.removeItem(APP_ENTRY_URL);
};

type TShouldDismissArgs = { appEntryUrl: string | null; locationHref: string };

/**
 * `setWebViewEntryURL()`로 저장한 최초 진입 URL과 현재 URL이 같으면 웹뷰를 닫는다.
 * 아니면 `forceBackFromExternal()`.
 * @param path
 * @param exclude
 * @param shouldDismiss 웹뷰를 닫을지 여부를 결정하는 함수. ture면 dismiss, false면 history.back
 * 기본값은 최초 진입 URL과 현재 URL이 같으면 닫는다.
 */
export const forceBackWebView = (
  path: string,
  exclude?: RegExp,
  shouldDismiss?: (args: TShouldDismissArgs) => boolean,
): void => {
  const yaNative = getYaNative();
  if (!yaNative.isEnabled) {
    return forceBackFromExternal(path, exclude);
  }
  const _shouldDismiss =
    shouldDismiss || (({ appEntryUrl, locationHref }: TShouldDismissArgs) => appEntryUrl === locationHref);

  const entryURL = sessionStorage.getItem(APP_ENTRY_URL);
  _shouldDismiss({ appEntryUrl: entryURL, locationHref: location.href }) ? yaNative.dismiss() : history.back();
};

/**
 * 현재 접속한 페이지의 URL을 가져온다. SEO - canonical 속성을 위함
 * 서버사이드 랜더링일 시 `req.requestedUrl` 을 사용하나, 클라이언트 사이드로 들어올 시 `window.location` 객체를 활용한다
 */
export function getPageUrl(req: IRequestWrap): string {
  return req ? req.requestedUrl : `${window.location.origin}${window.location.pathname}`;
}

/**
 * IRequestError의 statusCode가 400 이상 500 미만인지 확인한다.
 * statusCode가 없으면 false를 반환한다.
 * @param error
 */
export function isClientError(error: IRequesterError): boolean {
  const statusCode = error.statusCode;
  return !!(statusCode && statusCode >= 400 && statusCode < 500);
}

/**
 * 주어진 문자열을 클립보드에 복사한다.
 * @param anything 클립보드에 복사할 내용
 */
export const copyToClipboard = async (text: string, afterExecute?: () => void): Promise<void> => {
  await copy(text);
  if (afterExecute) afterExecute();
};

/**
 * device의 종류에 따라 리다이렉션 될 페이지 링크를 다르게 설정한다.
 * @param device
 */
export function redirectToInstallPage(device: EN_APP_DEVICE | null): void {
  if (device === EN_APP_DEVICE.IOS) {
    return void (location.href = IOS_INSTALL);
  }

  if (device === EN_APP_DEVICE.ANDROID) {
    return redirectTo(ANDROID_INSTALL, device, ANDROID_INSTALL_WEB);
  }

  // 이미 /app/install이면, macOS일 때 App Store, 아닐 때 Play Store
  if (location.pathname === '/app/install') {
    const ua = new UAParser();
    return void (ua.getOS().name === 'Mac OS' ? (location.href = IOS_INSTALL) : (location.href = ANDROID_INSTALL_WEB));
  }

  // 나머지 모두 /app/install
  location.href = '/app/install';
}

/** 딥링크용 스마트 배너 URL을 생성하기 위한 파라미터 */
type SmartBannerParams = { type: 'native'; path: 'home' | 'etc' } | { type: 'hybrid'; path: string };
// sunny 의 ts 버전이 4.0 으로 낮아서 Template Literal Types 사용 불가..
// | { type: 'hybrid'; path: `https://${string}` };

/**
 * NOL 앱으로 딥링크하기 위한 디퍼드 링크를 생성합니다.
 * @param params 디퍼드 링크 타입과 경로
 */
export function generateDeferredLink(params: SmartBannerParams): string {
  const { type, path } = params;

  const query = qs.stringify(
    {
      via: 'mweb',
      viaCgntId: Cookies.get('cgntId'),
      trackcode: Cookies.get('trackcode'),
    },
    { addQueryPrefix: true, skipNulls: true, arrayFormat: 'comma' },
  );

  const appDeepLinkUri = (() => {
    if (type === 'native') {
      return `yanoljamotel://${path}${query}`;
    }
    // type === 'hybrid'
    const finalHybridUrl = path.includes('?') ? `${path}${query.replace('?', '&')}` : `${path}${query}`;
    return `yanoljamotel://wv?url=${encodeURIComponent(finalHybridUrl)}`;
  })();

  /** AppsFlyer OneLink URL에 전달할 파라미터 */
  const oneLinkParams = qs.stringify(
    {
      pid: 'smartbanner', // 캠페인 식별자
      af_click_lookback: '1d', // 클릭 후 리타게팅 유효 기간
      is_retargeting: 'true', // 리타게팅 링크 여부
      af_reengagement_window: '30d', // 리인게이지먼트(재참여) 유효 기간
      af_force_deeplink: 'true', // 앱 강제 실행 옵션
      af_dp: appDeepLinkUri, // 주 딥링크 주소
      deep_link_value: appDeepLinkUri, // 백업 딥링크 값 (af_dp와 동일)
    },
    { addQueryPrefix: true, skipNulls: true },
  );

  return `https://yanolja.onelink.me/tmFm${oneLinkParams}`;
}

/**
 * 주의: pc환경일 경우 해당 로직을 사용하지 않는다.
 * 즉, 모바일 환경이면서 브라우저일때에만 사용하도록 설계된 로직
 *
 * device와 link를 받아 link로 리다이렉션을 시킨다
 * link가 딥링크일 경우, 어플리케이션이 설치가 되지 않은 경우는 link로의 리다이렉션이 동작하지 않는다.
 * 미설치인 경우 설치 페이지(failoverLink)로 이동시킨다.
 *
 * @param link 리다이렉션 시킬 주소. 딥링크 주소일 수도 있고 다른 주소가 들어올 수도 있다.
 * @param device device 정보
 * @param failoverLink link 로 이동이 실패했을 경우에 대체로 보내고자하는 주소. 없으면 앱 다운로드 권장 페이지로 넘어간다.
 */
export function redirectTo(link: string, device: EN_APP_DEVICE | null, failoverLink?: string | null): void {
  const yaNative = getYaNative();
  if (!yaNative.isEnabled) {
    redirectToWithCheckTimer(link, device, failoverLink);
    return;
  }
  yaNative.canOpen({ link }, (completion, ret) => {
    // 인터페이스 미지원이거나 판단 불가인 경우 기존 방식으로 이동
    if (!completion) {
      redirectToWithCheckTimer(link, device, failoverLink);
      return;
    }
    // 이동 가능
    if (ret?.installed) {
      window.location.href = link;
      return;
    }
    // 미설치되어 이동 불가
    if (failoverLink) {
      try {
        window.location.href = failoverLink;
      } catch (e) {
        redirectToInstallPage(device);
      }
    } else {
      redirectToInstallPage(device);
    }
  });
}

/**
 * device와 link를 받아 link로 리다이렉션을 시킨다
 * link가 딥링크일 경우, 어플리케이션이 설치가 되지 않은 경우는 link로의 리다이렉션이 동작하지 않는다.
 * 그럴 때는 setTimeout이 1500ms후에 발동되어 device의 종류에 따라 어플리케이션 설치 페이지로 리다이렉션된다.
 * link가 딥링크일 경우, 어플리케이션이 설치가 되어 있는 경우는 link로의 리다이렉션이 정상적으로 이루어지고
 * setTimeout은 url이 교체될 때 메모리와 함께 내려가서 실행되지 않게 된다.
 * @param link 리다이렉션 시킬 주소. 딥링크 주소일 수도 있고 다른 주소가 들어올 수도 있다.
 * @param device device 정보
 * @param failoverLink link 로 이동이 실패했을 경우에 대체로 보내고자하는 주소. 없으면 앱 다운로드 권장 페이지로 넘어간다.
 */
export function redirectToWithCheckTimer(
  link: string,
  device: EN_APP_DEVICE | null,
  failoverLink?: string | null,
): void {
  if (!device) {
    redirectToInstallPage(null);
    return;
  }

  const clickedAt = +new Date();

  window.setTimeout(() => {
    if (+new Date() - clickedAt < 2000 && !window.document.hidden) {
      if (failoverLink) {
        try {
          window.location.href = failoverLink;
        } catch (error) {
          redirectToInstallPage(device);
        }
      }

      redirectToInstallPage(device);
    }
  }, 1500);
  window.location.href = link;
}

/**
 * 로그 필드 중에 값이 없으면 필드를 없애야 할 경우 쓰인다.
 * 로그 정의서에서 키값 뒤에 ?표가 있는 경우($tabName?)
 */
export const optionalLogFilter = filter(
  (n) =>
    n === 0 ||
    n === false ||
    (!!n && typeof n !== 'object') ||
    (Array.isArray(n) && hasItems(n)) ||
    (typeof n === 'object' && !!Object.keys(n || {}).length),
);

/**
 * 웹뷰로 띄워서 보여줄 페이지에 대한 로그인 처리를 해야할 때 사용하는 메소드
 * yaNative 를 사용하기 때문에 앱 역시 로그인을 동기화 시킬 수 있음
 */
export const webViewLoginSyncApp = (): void => {
  const yaNative = getYaNative();

  if (yaNative.isEnabled) {
    yaNative.login();
  } else {
    const redirect = location.pathname + location.search;
    location.replace(`/login?redirect=${redirect}`);
  }
};

/**
 * 오브젝트의 value 를 원하는 값으로 일괄 변경
 * 변환된 오브젝트의 키값에 대한 type hint 를 유지하기 위해 유틸함수를 별도로 작성
 * obj 가 변경되므로 복사복을 사용할 것인지에 대한 판단이 필요함!
 * @param obj 변경하고자 하는 오브젝트
 * @param desiredValue 변경하고자 하는 값
 */
export const changeAllValues = <T extends Record<string, any>, U extends keyof T, V>(
  obj: T,
  desiredValue: V,
): Record<U, V> => Object.keys(obj).reduce((acc, key) => ({ ...acc, [key]: desiredValue }), {} as Record<U, V>);

/**
 * string 이 최대 길이보다 클 때 자르고 말줄임표를 사용할 때 이용
 * 말줄임표는 '...' 문자열이 아닌 html entity 로 unicode 1 char 를 차지하며
 * 말줄임표 추가 시 끝에 대체될 문자 수 할당 가능
 */
export const truncate = (
  { replacedNum = 0, len = Infinity }: { replacedNum: number; len: number },
  str: string,
): string => {
  if (!str) return '';
  return str.length > len ? `${str.slice(0, len - replacedNum)}…` : str;
};

/**
 * 모바일 및 태블릿 기기 여부에 따라 'mweb' 혹은 'web' 값 반환
 */
export const getWebType = (): 'mweb' | 'web' => {
  const device = new UAParser().getDevice();
  return device.type === 'mobile' || device.type === 'tablet' ? 'mweb' : 'web';
};

export const isMWeb = (): boolean => getWebType() === 'mweb';
export const isWeb = (): boolean => getWebType() === 'web';

/**
 * object literal 인 object 여부
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const isPlainObject = (value: any): boolean => Object.prototype.toString.call(value) === '[object Object]';

/**
 * nested object 경우 재귀적으로 pred 를 수행하여 반환
 */
export const hasValueDeep = curry((pred: (arg: any) => boolean, obj: any): boolean => {
  if (any(pred, Object.values(obj))) {
    return true;
  }

  for (const key in obj) {
    if (isPlainObject(obj[key]) && hasValueDeep(pred, obj[key])) {
      return true;
    }
  }

  return false;
});

/**
 * nested object 경우 재귀적으로 mapObj 를 수행
 * value 값이 object literal 이라면 변환되지 않고 그 값으로 다시 mapObj 진행
 */
export const mapObjDeep = curry((fn: (arg: any) => any, obj: any): any =>
  map((v) => (isPlainObject(v) ? mapObjDeep(fn, v) : fn(v)))(obj),
);

/**
 * 현재 실행 모드를 가져온다.
 * server 에서는 process.env.RUN_MODE 를 사용하고, client 에서는 window.ya__.runMode 를 사용한다.
 * @returns {string} 실행 모드
 * @example
 * getRunMode(); // 'local' | 'development' | 'qa' | 'stage' | 'production'
 */
export const getRunMode: () => keyof EnvConfig = () => {
  if (checkIsBrowser() && !!window?.ya__?.runMode) {
    return window.ya__.runMode as keyof EnvConfig;
  }
  return process.env.RUN_MODE as keyof EnvConfig;
};

/**
 * 쿼리스트링을 파싱하여 문자열(string)로 변환
 * string[] 타입의 쿼리스트링 값이 있을 경우 마지막 값을 반환
 * 빈 문자열('')은 제외하고 판단
 * 파싱 과정에서 value 는 url decode 처리
 * @param queryKey 쿼리스트링 키
 * @param queryValue 쿼리스트링 값
 * @param shouldDecode url decode 여부
 * @returns 문자열로 변환된 쿼리스트링 값
 */
export const parseQueryToString = (queryValue: ParsedQs[number], shouldDecode: boolean = false): string | undefined => {
  try {
    return qs.parse(
      qs.stringify(
        { queryKey: queryValue },
        {
          arrayFormat: 'repeat',
          filter: (_, val) => (val ? val : undefined), // 빈 문자열('') 제거
        },
      ),
      {
        ignoreQueryPrefix: true,
        duplicates: 'last',
        decoder: (str, defaultDecoder, charset, type) => {
          if (shouldDecode && type === 'value') {
            return decodeURIComponent(decodeURIComponent(str));
          }
          return defaultDecoder(str, defaultDecoder, charset);
        },
      },
    )['queryKey'] as string | undefined;
  } catch (error) {
    return undefined;
  }
};

/**
 * 쿼리 객체를 파싱하여 key-value 쌍으로 변환.
 * 쿼리스트링 값이 string[] 타입일 경우 마지막 값을 반환
 * string | string[] | undefined 타입의 쿼리를 string | undefined 타입으로 변환
 * 빈 문자열('')은 제외하고 판단
 * @param query 쿼리 객체
 * @param shouldDecode url decode 여부
 * @returns 파싱된 결과 객체
 */
export const parseQueryObject = (
  query: ParsedQs,
  shouldDecode: boolean = false,
): { [key: string]: string | undefined } => {
  const result: { [key: string]: string | undefined } = {};

  for (const key in query) {
    if (query.hasOwnProperty(key)) {
      result[key] = parseQueryToString(query[key], shouldDecode);
    }
  }

  return result;
};

/**
 * nextjs 동적 path 에서 동적 세그먼트를 추출한다.
 * ex) /place/[name] -> ['name']
 * ex) /place/[name]/[id] -> ['name', 'id']
 */
export const getNextjsDynamicSegments = (pathname: string): string[] => {
  const regex = /\[([^\]]+)\]/g;
  const segments: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(pathname)) !== null) {
    segments.push(match[1]);
  }

  return segments;
};

/** input value에서 숫자만 추출하여 반환한다. */
export const getOnlyDigits = (val: string): string => {
  return val.replace(/[^0-9]/g, '');
};

/**
 * 현재 해시 값을 유지하면서 새로운 URL로 히스토리 상태를 교체합니다.
 * 이 함수는 현재 위치의 해시 부분(e.g., "#section1")을 유지하면서 브라우저의 히스토리를 새로운 URL로 업데이트합니다.
 */
export const replaceHistoryState = (url: string): void => {
  const { hash } = new URL(location.href);
  history.replaceState(null, '', `${url}${hash}`);
};
